// "8:10 AM" from an ISO timestamp; otherwise the value as given.
//
// #688 — scheduledTime is genuine UTC (ADODB parses Access's naive time as
// box-local, and foxbox reads it back with UTC getters — correct only because
// fvdts sits in the venue's timezone). Whatever Access/FVD shows IS the truth,
// so we render in the venue zone (America/Los_Angeles), matching
// scoreadmin-fe's classDisplay.js and the printed day sheet. NOT 'UTC' (that
// was this bug: it printed 4:44 PM where the day sheet shows 9:44 AM) and NOT
// browser-local (wrong for off-site viewers and in the report Lambda).
const VENUE_TIME_ZONE = "America/Los_Angeles";

export function formatTime(v) {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v))) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: VENUE_TIME_ZONE,
      });
    }
  }
  return String(v);
}

// #691 — the sticky header's live "time of day" clock. Always the VENUE's
// wall clock (America/Los_Angeles), same #688 decision as formatTime above:
// an announcer standing in the ring must see the time the ring is on, not
// wherever his own browser happens to sit. Distinct from formatClock.js's
// formatClockTime, which is deliberately BROWSER-local — that one is for
// client-side "this happened just now" strings like "last updated".
export function formatVenueClock(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZone: VENUE_TIME_ZONE,
  });
}

// #892 — the venue-local CALENDAR DAY ("2026-07-17") a timestamp falls on,
// same #688/#805 conversion as formatTime/formatVenueClock above (genuine UTC
// in, rendered through the venue zone). en-CA is just a locale whose default
// date format happens to BE "YYYY-MM-DD" — a convenient, dependency-free way
// to pull a sortable/comparable key out of Intl instead of hand-rolling one.
// Deliberately NOT a naive string-slice of the ISO value (that's the OLDER,
// now-stale #634 report-defs convention from before foxbox emitted genuine
// UTC, #805) — a ride stamped late evening venue-local can be the NEXT day in
// UTC, and this must still group it under the venue day the day sheet shows.
export function venueDayKey(v) {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: VENUE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

// "today" as the VENUE sees it right now — never the browser's own zone. An
// announcer's laptop set to some other timezone must still agree with
// everyone standing at the venue about what day it is.
export function venueTodayKey(now = new Date()) {
  return venueDayKey(now);
}

// "THURSDAY" from a venueDayKey ("2026-07-17"). Deliberately derived from the
// KEY STRING, not by re-parsing the original timestamp through the venue
// timezone a second time: noon-padding a bare date string
// (`${key}T12:00:00`) sidesteps any zone/DST edge shifting the calendar date,
// same trick report-defs' class-day.js dayHeading() already uses for this
// exact reason.
export function dayKeyLabel(dayKey) {
  if (!dayKey) return "";
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
}

export default formatTime;
